// Constants
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
// Controls
import { DirectionalHint, FocusTrapZone, Icon } from "@fluentui/react";
import { Calendar } from "../calendar/Calendar";
import { Callout } from "../callout/Callout";
// Styles
import { useTheme } from "@fluentui/react";
import { calendarIconProps, recurrenceIconProps } from "../../../icons/icons.styles";
import { getClassNames, getStyles } from "./DueDateIndicator.styles";
// Utilities
import { css } from "@fluentui/utilities/lib/css";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import * as React from "react";
import { useVisibleState } from "../../../hooks/useVisibleState";
/**
 * DueDate Indicator component that renders an icon with the current dueDate of the task
 */ export const DueDateIndicator = (props)=>{
    const { isItemVisible: isDueDateCalendarVisible, toggleOnEvent: toggleDueDateCalendar, hideItem: dismissDueDateCalendar } = useVisibleState();
    const dueDateButtonElement = React.useRef(null);
    const { ariaLabel, isCurrentInstanceActive, isKeyboardFocusable, styles, taskPermissions, isLate, onKeyDown, onSetDueDate } = props;
    /**
     * Set the due date for the task
     * @param date The new due date to set
     */ const setDueDate = React.useCallback((date)=>{
        onSetDueDate(date);
        dismissDueDateCalendar();
    }, [
        dismissDueDateCalendar,
        onSetDueDate
    ]);
    /**
     * Handles key up events on due date control
     * @param event Keyboard interface event
     */ const handleKeyUpOnDueDate = React.useCallback((event)=>{
        const key = KeyboardUtilities.getKey(event);
        if (key === KeyCode.ESCAPE) {
            dismissDueDateCalendar();
        }
    }, [
        dismissDueDateCalendar
    ]);
    const theme = useTheme();
    const dueDateClassNames = getClassNames(getStyles(theme, styles));
    const className = React.useMemo(()=>css(dueDateClassNames?.root, !taskPermissions.canEdit || !taskPermissions.canUpdateDueDate ? null : "clickable", {
            isLate
        }), [
        dueDateClassNames?.root,
        taskPermissions.canEdit,
        taskPermissions.canUpdateDueDate,
        isLate
    ]);
    const calendarLabel = props.dueDate ? props.formattedDueDate : Strings.TaskCard_DueDatePlaceholder;
    const dueDateIconProps = isCurrentInstanceActive ? recurrenceIconProps : calendarIconProps;
    return /*#__PURE__*/ React.createElement("div", {
        ref: dueDateButtonElement,
        className: className,
        "aria-label": ariaLabel,
        title: ariaLabel,
        "data-is-focusable": isKeyboardFocusable,
        onClick: toggleDueDateCalendar,
        onKeyUp: handleKeyUpOnDueDate,
        onKeyDown: onKeyDown,
        role: "button"
    }, /*#__PURE__*/ React.createElement(Icon, {
        ...dueDateIconProps,
        className: css(dueDateClassNames?.calendarIcon, dueDateIconProps.className)
    }), /*#__PURE__*/ React.createElement("span", {
        className: css(dueDateClassNames?.calendarLabel, "label"),
        tabIndex: -1
    }, calendarLabel), taskPermissions.canUpdateDueDate && isDueDateCalendarVisible && /*#__PURE__*/ React.createElement(Callout, {
        isBeakVisible: false,
        gapSpace: 0,
        doNotLayer: false,
        target: dueDateButtonElement,
        directionalHint: DirectionalHint.bottomLeftEdge,
        onDismiss: dismissDueDateCalendar,
        setInitialFocus: true,
        className: getClassNames(getStyles(theme, styles)).callout
    }, /*#__PURE__*/ React.createElement(FocusTrapZone, {
        isClickableOutsideFocusTrap: true,
        forceFocusInsideTrap: false
    }, /*#__PURE__*/ React.createElement(Calendar, {
        onSelectDate: setDueDate,
        onDismiss: dismissDueDateCalendar,
        isMonthPickerVisible: true,
        value: props.dueDate != null ? props.dueDate : undefined,
        today: new Date()
    }))));
};
